"use client";

import { cdn } from "@/lib/cdn";
import { BookOpen, Link2, MessageSquare, QrCode, ThumbsUp } from "lucide-react";
import { ReactionsMock } from "./analytics-mocks";
import {
  LIVE_READ_LEFT_PAGES,
  LIVE_READ_PAGES,
  LIVE_READ_RIGHT_PAGES,
  LIVE_SCAN_PAGES,
  PIPELINE_SLIDE,
  REVENUE_SLIDE,
} from "./live-pages";
import {
  LiveCommentBar,
  LiveDevice,
  LivePhone,
  LiveQrCard,
} from "./live-visuals";
import { MockLabel } from "./mock-label";
import { FloatingCard, PhotoStage } from "./showcase";
import { ShowcaseTabs, type ShowcaseTab } from "./showcase-tabs";

function ScanStage() {
  return (
    <PhotoStage
      src={cdn("qr/stage_aud.png")}
      alt="An audience in front of a large screen"
      className="mx-auto h-[560px] w-full max-w-[1040px] rounded-xl border border-neutral-200"
      imageClassName="object-[50%_62%]"
    >
      <LiveQrCard className="absolute top-[13%] left-1/2 w-32 -translate-x-1/2 sm:w-40" />
      <FloatingCard className="absolute top-5 left-5 hidden items-center gap-2 px-3 py-2 text-[13px] text-neutral-700 sm:inline-flex">
        <Link2 className="size-3.5 shrink-0 text-neutral-500" />
        handyout.com/p/…
      </FloatingCard>
      <LiveDevice
        pages={LIVE_SCAN_PAGES}
        scale={0.62}
        autoAdvanceMs={4500}
        className="absolute top-[30%] right-8 hidden md:block"
      />
    </PhotoStage>
  );
}

function ReadStage() {
  return (
    <div className="flex justify-center gap-6 lg:gap-10">
      <LiveDevice
        pages={LIVE_READ_LEFT_PAGES}
        scale={0.78}
        autoAdvanceMs={5200}
        className="mt-16 hidden lg:block"
      />
      <LiveDevice pages={LIVE_READ_PAGES} scale={0.78} autoAdvanceMs={4500} />
      <LiveDevice
        pages={LIVE_READ_RIGHT_PAGES}
        scale={0.78}
        autoAdvanceMs={5600}
        className="mt-16 hidden sm:block"
      />
    </div>
  );
}

function ReactStage() {
  return (
    <div className="flex items-start justify-center gap-8 lg:gap-14">
      <LivePhone slide={REVENUE_SLIDE} scale={0.64} />
      <div className="hidden w-[320px] flex-col gap-4 pt-16 md:flex">
        <FloatingCard className="p-4">
          <MockLabel icon={MessageSquare} aside={`Page ${REVENUE_SLIDE.pageNumber}`}>
            {REVENUE_SLIDE.title}
          </MockLabel>
          <LiveCommentBar slide={REVENUE_SLIDE} />
        </FloatingCard>
        <FloatingCard className="p-4">
          <ReactionsMock />
        </FloatingCard>
      </div>
    </div>
  );
}

function CommentStage() {
  return (
    <div className="flex items-start justify-center gap-6 lg:gap-14">
      <LivePhone
        slide={PIPELINE_SLIDE}
        scale={0.64}
        className="mt-10 hidden sm:block"
      />
      <LivePhone slide={PIPELINE_SLIDE} open scale={0.64} />
    </div>
  );
}

const TABS: ShowcaseTab[] = [
  {
    value: "scan",
    icon: <QrCode />,
    title: "Scan",
    text: "One QR per handyout, on your slide or full screen. It opens in any browser.",
    content: <ScanStage />,
  },
  {
    value: "read",
    icon: <BookOpen />,
    title: "Read",
    text: "Each slide fills the screen. Swipe up for the next, tap a chart for its values.",
    content: <ReadStage />,
  },
  {
    value: "react",
    icon: <ThumbsUp />,
    title: "React",
    text: "Thumbs up or down on every slide. The counts update live for everyone.",
    content: <ReactStage />,
  },
  {
    value: "comment",
    icon: <MessageSquare />,
    title: "Comment",
    text: "Every slide has its own comments, with replies and likes.",
    content: <CommentStage />,
  },
];

export function LiveJourney() {
  return <ShowcaseTabs tabs={TABS} />;
}
